import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import {
  type PayoutRule,
  type TournamentRuleType,
  type VenuePayoutRule,
  ruleLabels,
  ruleTypes,
  validPayoutRule,
} from './payoutRule.ts';

function RuleEditor({
  value,
  onChange,
  type,
}: {
  type: TournamentRuleType;
  value: PayoutRule;
  onChange: (rule: PayoutRule) => void;
}) {
  const { t } = useTranslation('tournament');
  const patchPool = (key: keyof PayoutRule['pool'], input: number) =>
    onChange({ ...value, pool: { ...value.pool, [key]: input } });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        {type === 'TIMED' ? (
          <div className="text-sm sm:col-span-2">
            <p>{t('timedRankReserve', { rate: value.pool.rankBonusPercent })}</p>
            <p>{t('timedBaseFormula')}</p>
            {value.pool.rankBonusPercent !== 3 && (
              <div role="alert" className="mt-2 text-warning">
                <p>{t('timedLegacyRate')}</p>
                <button
                  type="button"
                  className="mt-2 rounded border px-3 py-2"
                  onClick={() =>
                    onChange({ ...value, pool: { primaryPercent: 97, rankBonusPercent: 3 } })
                  }
                >
                  {t('timedUseApprovedRate')}
                </button>
              </div>
            )}
          </div>
        ) : (
          (
            [
              ['primaryPercent', '主要獎金比例 (%)'],
              ['rankBonusPercent', '名次獎金比例 (%)'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="text-sm">
              {label}
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                className="mt-1 w-full rounded border p-2"
                value={value.pool[key]}
                onChange={(event) => patchPool(key, Number(event.target.value))}
              />
            </label>
          ))
        )}
        <div className="text-sm">
          向下取整單位
          <p className="mt-1 rounded border p-2">{value.roundingUnit}</p>
        </div>
      </div>
      <p className="text-xs text-base-content/70">
        {type === 'TIMED'
          ? t('timedResidualExplanation')
          : '兩項獎池比例合計須為 100%；每組名次比例也須合計為 100%。未分配尾數保留在結算差額。'}
      </p>
      {value.rankTiers.map((tier, index) => (
        <div key={index} className="rounded border p-3 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm">
              參賽人數上限
              <input
                type="number"
                min="1"
                step="1"
                disabled={tier.maxEntrants === null}
                className="ml-2 w-24 rounded border p-1"
                value={tier.maxEntrants ?? ''}
                onChange={(event) =>
                  onChange({
                    ...value,
                    rankTiers: value.rankTiers.map((row, rowIndex) =>
                      rowIndex === index
                        ? { ...row, maxEntrants: Number(event.target.value) }
                        : row,
                    ),
                  })
                }
              />
            </label>
            {tier.maxEntrants === null && <span className="text-xs">以上人數</span>}
            {tier.maxEntrants !== null && (
              <button
                type="button"
                className="text-error text-sm"
                onClick={() =>
                  onChange({
                    ...value,
                    rankTiers: value.rankTiers.filter((_, rowIndex) => rowIndex !== index),
                  })
                }
              >
                移除
              </button>
            )}
          </div>
          <label className="block text-sm">
            各名次比例（逗號分隔，依名次順序）
            <input
              className="mt-1 w-full rounded border p-2"
              value={tier.shares.join(', ')}
              onChange={(event) =>
                onChange({
                  ...value,
                  rankTiers: value.rankTiers.map((row, rowIndex) =>
                    rowIndex === index
                      ? {
                          ...row,
                          shares: event.target.value.split(',').map((item) => Number(item.trim())),
                        }
                      : row,
                  ),
                })
              }
            />
          </label>
        </div>
      ))}
      <button
        type="button"
        className="rounded border px-3 py-2 text-sm"
        onClick={() =>
          onChange({
            ...value,
            rankTiers: [
              ...value.rankTiers.slice(0, -1),
              {
                maxEntrants: (value.rankTiers.at(-2)?.maxEntrants ?? 0) + 10,
                shares: [50, 30, 20],
              },
              value.rankTiers.at(-1)!,
            ],
          })
        }
      >
        新增人數級距
      </button>
    </div>
  );
}

export { RuleEditor };

export default function PayoutRuleSettingsPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<TournamentRuleType>('TIMED');
  const [draft, setDraft] = useState<PayoutRule | null>(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['venue-payout-rules'],
    queryFn: () => api.get<{ rules: VenuePayoutRule[] }>('/tournament/payout-rules'),
  });
  const current = data?.rules.find((item) => item.type === selected);
  useEffect(() => {
    if (current) setDraft(structuredClone(current.rule));
  }, [current]);
  const save = useMutation({
    mutationFn: () =>
      api.put(`/tournament/payout-rules/${selected}`, {
        expectedVersion: current?.version ?? 0,
        reason: reason.trim(),
        rule: draft,
      }),
    onSuccess: async () => {
      setMessage('已儲存新的場館規則版本。');
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['venue-payout-rules'] });
    },
    onError: (failure: Error) => setMessage(failure.message),
  });
  return (
    <div className="mx-auto max-w-4xl space-y-5 p-6">
      <Link to="/tournament" className="text-info text-sm">
        返回賽事
      </Link>
      <h1 className="text-2xl font-bold">獎池分配規則</h1>
      <p className="text-sm text-base-content/70">
        場館預設只影響之後建立的賽事；既有賽事保留建立時的規則快照。
      </p>
      <label className="block text-sm">
        賽事類型
        <select
          className="ml-3 rounded border p-2"
          value={selected}
          onChange={(event) => setSelected(event.target.value as TournamentRuleType)}
        >
          {ruleTypes.map((type) => (
            <option key={type} value={type}>
              {ruleLabels[type]}
            </option>
          ))}
        </select>
      </label>
      {isLoading && <p>載入中…</p>}
      {error && (
        <p role="alert" className="text-error">
          {(error as Error).message}
        </p>
      )}
      {current && draft && (
        <section className="space-y-4 rounded-xl border p-4">
          <p className="text-sm">目前版本：{current.version}</p>
          <RuleEditor type={selected} value={draft} onChange={setDraft} />
          <label className="block text-sm">
            變更原因
            <input
              className="mt-1 w-full rounded border p-2"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {!validPayoutRule(draft, selected) && (
            <p role="alert" className="text-error text-sm">
              比例或人數級距無效，請檢查各層合計。
            </p>
          )}
          <button
            type="button"
            className="rounded bg-primary px-4 py-2 text-primary-content disabled:opacity-50"
            disabled={!validPayoutRule(draft, selected) || !reason.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            儲存新版本
          </button>
          {message && (
            <p role="status" className="text-sm">
              {message}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
